import { alpha, Box, type SxProps, type Theme, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { ALPHA, FONT_SIZE, LETTER_SPACING, MOTION, NEUTRAL } from "../../../theme/tokens";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";

/** Where a stage sits relative to the thing being tracked. The CALLER decides — see the component doc. */
export type StageTrackState = "done" | "current" | "upcoming";

/** Semantic tone, resolved against the theme palette (never a hardcoded hex). */
export type StageTrackTone = "primary" | "success" | "warning" | "error" | "info";

export type StageTrackStage = {
	id: string;
	/** Already-translated stage name. */
	label: string;
	state: StageTrackState;
	/** Optional hover detail (e.g. when the stage was reached). */
	tooltip?: string;
	dataTestId?: string;
};

/**
 * A terminal branch the tracked thing left the track on — an outcome that is NOT further along the
 * sequence (a candidate disqualified, a request withdrawn, a submission rejected). Rendered as a
 * detached trailing segment in its own tone so it can never be mistaken for the last happy stage.
 */
export type StageTrackOffRamp = {
	/** Already-translated outcome name. */
	label: string;
	tone: StageTrackTone;
	tooltip?: string;
	dataTestId?: string;
};

type Props = {
	/** The ordered sequence, first → last. Each stage carries its own `state`. */
	stages: StageTrackStage[];
	/** Optional terminal branch appended after the sequence. */
	offRamp?: StageTrackOffRamp;
	/** Tone for the done/current segments (default `primary`). The off-ramp carries its own. */
	tone?: StageTrackTone;
	/** Accessible name for the whole track (already translated) — e.g. "Hiring stage". */
	ariaLabel: string;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

const BAR_HEIGHT = { REST: 4, CURRENT: 7 } as const;

/** Per-segment left-to-right grow-in. Transform-only (GPU) and dropped entirely under reduced motion. */
const ENTRANCE_SX: SxProps<Theme> = {
	"@keyframes stageTrackGrow": {
		from: { transform: "scaleX(0)" },
		to: { transform: "scaleX(1)" },
	},
	transformOrigin: "left center",
	animation: `stageTrackGrow ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT} both`,
	"@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const toneOf = (theme: Theme, tone: StageTrackTone) => theme.palette[tone];

/**
 * A labelled **stage track**: an ordered sequence of named stages rendered as connected segments with the
 * current one called out. The "where is this in its lifecycle" instrument — a hiring funnel stage, an
 * approval chain, an onboarding sequence, a submission pipeline.
 *
 * Purely presentational and prop-driven: the caller owns every label (already translated) and decides what
 * `done` / `current` / `upcoming` mean for its domain, so the track can never disagree with the status
 * printed beside it. An outcome that is a terminal *branch* rather than a later stage goes in `offRamp`,
 * which renders detached and in its own tone — never as "the last stage, reached".
 *
 * Auto-compacts (like `SystemSideRail`): below `sm` the per-stage labels would each get ~55px and truncate
 * to noise, so they are dropped and a single line names the current stage instead. The bars, the
 * `ariaLabel`, and each segment's `aria-current` are unchanged, so nothing is lost to a screen reader.
 *
 * For a continuous 0–100 value with graded checkpoints use `MilestoneProgressBar`; for one number against a
 * scale use `SystemLinearMeter` / `SystemRadialGauge`.
 */
export const SystemStageTrack: FC<Props> = ({ stages, offRamp, tone = "primary", ariaLabel, dataTestId, sx }) => {
	const theme = useTheme();
	const accent = toneOf(theme, tone);
	const offRampAccent = offRamp ? toneOf(theme, offRamp.tone) : null;
	// The compact (xs) summary line names whichever segment is live — the off-ramp wins, since leaving the
	// track is the more important fact than how far along it the thing got.
	const liveLabel = offRamp?.label ?? stages.find((stage) => stage.state === "current")?.label;

	const segmentFill = (state: StageTrackState) => {
		if (state === "current") return accent.main;
		if (state === "done") return alpha(accent.main, ALPHA.STAGE_DONE);
		return NEUTRAL[200];
	};

	const labelSx = (state: StageTrackState): SxProps<Theme> => ({
		fontSize: FONT_SIZE.LABEL_XS,
		fontWeight: state === "current" ? 700 : 500,
		letterSpacing: LETTER_SPACING.LABEL,
		textTransform: "uppercase",
		lineHeight: 1.3,
		color: state === "current" ? accent.dark : state === "done" ? "text.secondary" : "text.disabled",
		display: { xs: "none", sm: "block" },
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	});

	return (
		<Box data-testid={dataTestId} role="group" aria-label={ariaLabel} sx={[{ width: "100%", minWidth: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}>
			<Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.75 }}>
				{stages.map((stage, index) => (
					<InHouseTooltip key={stage.id} title={stage.tooltip ?? ""}>
						<Box
							data-testid={stage.dataTestId}
							data-stage-state={stage.state}
							aria-current={stage.state === "current" ? "step" : undefined}
							sx={{ flex: "1 1 0", minWidth: 0, display: "flex", flexDirection: "column", gap: 0.75 }}
						>
							<Box
								sx={[
									ENTRANCE_SX,
									{
										height: stage.state === "current" ? BAR_HEIGHT.CURRENT : BAR_HEIGHT.REST,
										borderRadius: 99,
										backgroundColor: segmentFill(stage.state),
										animationDelay: `${Math.min(index * MOTION.STAGGER.STEP, MOTION.STAGGER.MAX)}ms`,
									},
								]}
							/>
							<Typography component="span" sx={labelSx(stage.state)}>
								{stage.label}
							</Typography>
						</Box>
					</InHouseTooltip>
				))}

				{offRamp && offRampAccent && (
					// Detached (an extra leading gap) and in its own tone: this is a branch OFF the sequence.
					<InHouseTooltip title={offRamp.tooltip ?? ""}>
						<Box
							data-testid={offRamp.dataTestId}
							data-stage-state="off-ramp"
							aria-current="step"
							sx={{ flex: "1 1 0", minWidth: 0, display: "flex", flexDirection: "column", gap: 0.75, ml: 0.75 }}
						>
							<Box
								sx={[
									ENTRANCE_SX,
									{
										height: BAR_HEIGHT.CURRENT,
										borderRadius: 99,
										backgroundColor: offRampAccent.main,
										animationDelay: `${MOTION.STAGGER.MAX}ms`,
									},
								]}
							/>
							<Typography
								component="span"
								sx={{
									fontSize: FONT_SIZE.LABEL_XS,
									fontWeight: 700,
									letterSpacing: LETTER_SPACING.LABEL,
									textTransform: "uppercase",
									lineHeight: 1.3,
									color: offRampAccent.dark,
									display: { xs: "none", sm: "block" },
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}
							>
								{offRamp.label}
							</Typography>
						</Box>
					</InHouseTooltip>
				)}
			</Box>

			{liveLabel && (
				<Typography
					component="span"
					sx={{
						display: { xs: "block", sm: "none" },
						mt: 0.75,
						fontSize: FONT_SIZE.LABEL_XS,
						fontWeight: 700,
						letterSpacing: LETTER_SPACING.LABEL,
						textTransform: "uppercase",
						color: offRampAccent ? offRampAccent.dark : accent.dark,
					}}
				>
					{liveLabel}
				</Typography>
			)}
		</Box>
	);
};
